import { useLoadRevisionPointCloudStreams } from "@/data-preparation-tool/loading/use-load-revision-point-cloud-streams";
import {
  selectConnectedIssueComponents,
  selectSelectedEntityIds,
  selectValidRevisionScans,
} from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-selectors";
import {
  RegistrationStatusMap,
  selectHierarchicalRegistrationStatusMap,
} from "@/data-preparation-tool/store/registration-status-selectors";
import {
  AtomicClusterMap,
  selectScansToAtomicClusterMap,
} from "@/data-preparation-tool/store/revision/revision-cluster-selectors";
import { selectAreEdgesLoading } from "@/data-preparation-tool/store/revision/revision-edge-selectors";
import { getConnectedEntitiesGroups } from "@/data-preparation-tool/store/selector-helpers";
import { sortByName } from "@/data-preparation-tool/utils/sorting";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  blue,
  FaroText,
  grey,
  TranslateVar,
  TruncatedFaroText,
  yellow,
} from "@faro-lotv/flat-ui";
import {
  CaptureTreeEntityRevision,
  isRevisionScanEntity,
} from "@faro-lotv/service-wires";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Stack,
} from "@mui/material";
import { uniqBy } from "es-toolkit";
import { useMemo, type JSX } from "react";
import { ReactComponent as BadgeIcon } from "../../icons/badge.svg";
import { doesEntityRequireReview } from "../../utils/entity-utils";
import { useEntityChildrenMap } from "../scan-tree/scan-tree";
import { ActionGroupAccordion } from "./action-group-accordion";
import { ConnectedEntitiesGroupsContext } from "./connected-entities-groups-context";

/**
 * @param entities The entities to use in search of the actions in the ActionsPanel.
 */
export type ActionsPanelProps = {
  entities: CaptureTreeEntityRevision[];
};

/** @returns a panel displaying the action items that exist with the scans */
export function ActionsPanel({
  entities,
}: ActionsPanelProps): JSX.Element | null {
  const statusMap = useAppSelector(selectHierarchicalRegistrationStatusMap);
  const areEdgesLoading = useAppSelector(selectAreEdgesLoading);

  const scanEntities = useAppSelector(selectValidRevisionScans);
  const pointCloudStreams = useLoadRevisionPointCloudStreams(scanEntities);
  const atomicClusterMap = useAppSelector(selectScansToAtomicClusterMap);

  const entitiesWithIssues = useMemo(
    () => getCapturesRequiringReview(entities, statusMap, atomicClusterMap),
    [entities, statusMap, atomicClusterMap],
  );
  const issueCount = entitiesWithIssues.length;

  if (issueCount === 0 || areEdgesLoading) {
    return null;
  }

  return (
    <>
      <Accordion
        aria-label="registration action panel"
        // Styled to match the design of the `RegistrationCard` component
        sx={{
          width: "100%",
          background: grey[50],
          boxShadow: "none",
          borderRadius: 0.5,
          border: "solid rgba(0, 0, 0, 0.12) 1px",
          overflow: "hidden",

          "&.Mui-expanded": {
            margin: 0,
          },
        }}
        slotProps={{
          heading: {
            sx: {
              "& .MuiAccordionSummary-root": {
                minHeight: "40px",
              },
              "& .MuiAccordionSummary-root.Mui-expanded": {
                minHeight: "40px",
              },
            },
          },
        }}
        defaultExpanded
      >
        <AccordionSummary
          aria-controls="panel-content"
          expandIcon={<ExpandMoreIcon />}
          sx={{
            "& .MuiAccordionSummary-content": {
              margin: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minWidth: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 1,
              // Don't wrap to next line on small sidebar, truncate text
              overflowX: "hidden",
            }}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                // Don't wrap to next line on small sidebar, truncate text
                overflowX: "hidden",
              }}
            >
              <TruncatedFaroText variant="bodyM" sx={{ pr: 0.5 }}>
                Action required
              </TruncatedFaroText>
              <FaroText variant="bodyM">
                (<TranslateVar name="issueCount">{issueCount}</TranslateVar>)
              </FaroText>
            </Stack>
            <BadgeIcon
              style={{
                color: yellow[500],
                width: 12,
                height: 12,
              }}
            />
          </Stack>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            display: "flex",
            flexDirection: "column",
            p: 0,
            gap: 1,
          }}
        >
          <FaroText
            variant="bodyS"
            component="div"
            sx={{ px: 2, fontSize: "12px", color: grey[600] }}
          >
            Position these captures to finalize the registration.
          </FaroText>

          <Stack
            sx={{
              gap: 1,
              px: 2,
              pb: 2,
              // Prevent action panel from taking too much space of the sidebar
              maxHeight: "20vh",
              // Only scroll the list of unconnected captures, to keep the help text at the top
              overflowY: "auto",
            }}
          >
            <ActionGroupList entities={entitiesWithIssues} />
            {pointCloudStreams &&
            pointCloudStreams.length !== scanEntities.length ? (
              <>
                <Divider />
                <FaroText
                  variant="bodyM"
                  component="div"
                  sx={{
                    backgroundColor: blue[50],
                    border: `1px solid ${grey[1300]}20`,
                    borderRadius: 0.5,
                    padding: 1,
                  }}
                >
                  Some captures are still being prepared for viewing. Please
                  reload the page after a while.
                </FaroText>
              </>
            ) : null}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Divider />
    </>
  );
}

/** @returns a panel displaying the issues that exist with the scans */
export function ActionGroupList({
  entities,
}: ActionsPanelProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const selectedEntityIds = useAppSelector(selectSelectedEntityIds);

  const connectedIssueComponents = useAppSelector(
    selectConnectedIssueComponents(entities),
  );

  const connectedEntitiesGroups = useMemo(() => {
    const connectedGroups = getConnectedEntitiesGroups(
      connectedIssueComponents,
      entities,
    );

    // Sort the inner groups alphabetically
    for (const group of connectedGroups) {
      group.sort(sortByName);
    }

    // Sort the groups by the first item
    connectedGroups.sort((groupA, groupB) => {
      const firstItemA = groupA.at(0);
      const firstItemB = groupB.at(0);
      if (!firstItemA || !firstItemB) return 0;

      return sortByName(firstItemA, firstItemB);
    });

    return connectedGroups;
  }, [connectedIssueComponents, entities]);

  const selectedActionPanelIds = useMemo(
    () =>
      entities
        .filter((entity) => selectedEntityIds.includes(entity.id))
        .map((entity) => entity.id),
    [entities, selectedEntityIds],
  );

  // Map for a quick children lookup by id
  const unconnectedEntityChildrenMap = useEntityChildrenMap(entities);

  return (
    <ConnectedEntitiesGroupsContext value={connectedEntitiesGroups}>
      <Stack sx={{ gap: 0 }}>
        {connectedEntitiesGroups.map((group, index) => (
          <ActionGroupAccordion
            key={index}
            group={group}
            groupIndex={index}
            selectedActionPanelIds={selectedActionPanelIds}
            unconnectedEntityChildrenMap={unconnectedEntityChildrenMap}
            dispatch={dispatch}
            selectedEntityIds={selectedEntityIds}
          />
        ))}
      </Stack>
    </ConnectedEntitiesGroupsContext>
  );
}

/**
 * @param entities The entities to extract names from.
 * @param statusMap A map from entity IDs to their registration statuses.
 * @param atomicClusterMap A map from scan IDs to their atomic cluster, if contained in one.
 * @returns The captures which require review, because they are not connected to the rest of the captures.
 */
export function getCapturesRequiringReview(
  entities: CaptureTreeEntityRevision[],
  statusMap: RegistrationStatusMap,
  atomicClusterMap: AtomicClusterMap,
): CaptureTreeEntityRevision[] {
  // Determine the review status on the individual scans
  const scansThatRequireReview = entities.filter(
    (entity) =>
      isRevisionScanEntity(entity) &&
      doesEntityRequireReview(entity, statusMap),
  );

  // Return captures instead of scans (special handling for SiteScape)
  return uniqBy(
    scansThatRequireReview.map((scan) => atomicClusterMap.get(scan.id) ?? scan),
    (entity) => entity.id,
  );
}
